/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { LIST_DETAILS, USER_DETAILS } from '../app/components/AppNavigator/FeatureLinks';
import { LIST_ID_PARAM } from '../app/pages/listDetails/ListDetails';
import { AUTHOR_ID_PARAM } from '../app/pages/userDetails/UserDetails';
import { getAppSetting } from '../config/AppSettings';

export type ContextType = {
    currentStage: string;
    searchInput: string;
    navigateToList: (listId: string) => void;
    navigateToUser: (userId: string) => void;
};

export const DEFAULT_CONTEXT: ContextType = {
    currentStage: 'Alpha',
    searchInput: '',
    navigateToList: () => ({}),
    navigateToUser: () => ({})
};

export const GlobalContext = createContext<ContextType>(DEFAULT_CONTEXT);

const GlobalContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const currentStage = getAppSetting('stage');

    const searchInput = searchParams.get(LIST_ID_PARAM) || searchParams.get(AUTHOR_ID_PARAM) || '';

    const navigateTo = (pathname: string) => navigate({ pathname, search: searchParams.toString() });

    const navigateToList = (listId: string) => {
        if (searchParams.has(AUTHOR_ID_PARAM)) {
            searchParams.delete(AUTHOR_ID_PARAM);
        }
        searchParams.set(LIST_ID_PARAM, listId);
        navigateTo(LIST_DETAILS.link);
    };

    const navigateToUser = (userId: string) => {
        if (searchParams.has(LIST_ID_PARAM)) {
            searchParams.delete(LIST_ID_PARAM);
        }
        searchParams.set(AUTHOR_ID_PARAM, userId);
        navigateTo(USER_DETAILS.link);
    };

    return (
        <GlobalContext.Provider value={{ currentStage, searchInput, navigateToList, navigateToUser }}>
            {children}
        </GlobalContext.Provider>
    );
};

export function useGlobalContext() {
    return useContext<ContextType>(GlobalContext);
}

export default GlobalContextProvider;
